import {
  useEffect,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import type {
  FeedbackMark,
  FeedbackRichTextBlock,
  FeedbackRichTextDocument,
  FeedbackRichTextSpan,
} from '../../../server/feedback/contracts.js';
import { Button } from './Button.js';
import { PixelIcon } from '../PixelIcon.js';

export interface RichTextFieldProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'contentEditable' | 'onChange' | 'onInput'
> {
  readonly invalid?: boolean;
  readonly placeholder?: string;
  readonly resetKey?: string | number;
  readonly onChange: (value: FeedbackRichTextDocument) => void;
}

function sameMarks(left: readonly FeedbackMark[] | undefined, right: readonly FeedbackMark[]): boolean {
  return (left?.length ?? 0) === right.length && right.every((mark) => left?.includes(mark));
}

function appendSpan(
  spans: FeedbackRichTextSpan[],
  text: string,
  marks: readonly FeedbackMark[],
): void {
  if (text.length === 0) return;
  const normalized = text.replaceAll('\u00a0', ' ');
  const previous = spans.at(-1);
  if (previous !== undefined && sameMarks(previous.marks, marks)) {
    spans[spans.length - 1] = {
      text: `${previous.text}${normalized}`,
      ...(marks.length === 0 ? {} : { marks: [...marks] }),
    };
    return;
  }
  spans.push({ text: normalized, ...(marks.length === 0 ? {} : { marks: [...marks] }) });
}

function inlineSpans(node: Node, inheritedMarks: readonly FeedbackMark[] = []): FeedbackRichTextSpan[] {
  const spans: FeedbackRichTextSpan[] = [];
  const visit = (current: Node, marks: readonly FeedbackMark[]): void => {
    if (current.nodeType === Node.TEXT_NODE) {
      appendSpan(spans, current.textContent ?? '', marks);
      return;
    }
    if (!(current instanceof HTMLElement)) return;
    if (current.tagName === 'BR') {
      appendSpan(spans, '\n', marks);
      return;
    }
    const nextMarks = [...marks];
    if ((current.tagName === 'B' || current.tagName === 'STRONG') && !nextMarks.includes('bold')) {
      nextMarks.push('bold');
    }
    if ((current.tagName === 'I' || current.tagName === 'EM') && !nextMarks.includes('italic')) {
      nextMarks.push('italic');
    }
    current.childNodes.forEach((child) => visit(child, nextMarks));
  };
  visit(node, inheritedMarks);
  return spans;
}

function appendParagraphsFromText(blocks: FeedbackRichTextBlock[], text: string): void {
  const lines = text.replaceAll('\u00a0', ' ').split('\n');
  for (const line of lines) blocks.push({ type: 'paragraph', spans: line === '' ? [] : [{ text: line }] });
}

export function richTextDocumentFromElement(root: HTMLElement): FeedbackRichTextDocument {
  const blocks: FeedbackRichTextBlock[] = [];
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendParagraphsFromText(blocks, node.textContent ?? '');
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === 'UL' || node.tagName === 'OL') {
      [...node.children].forEach((item) => {
        if (item.tagName === 'LI') blocks.push({ type: 'bullet', spans: inlineSpans(item) });
      });
      return;
    }
    if (node.tagName === 'DIV' || node.tagName === 'P') {
      const lists = [...node.children].filter((child) => child.tagName === 'UL' || child.tagName === 'OL');
      if (lists.length === 0) {
        blocks.push({ type: 'paragraph', spans: inlineSpans(node) });
        return;
      }
      node.childNodes.forEach((child) => {
        if (child instanceof HTMLElement && (child.tagName === 'UL' || child.tagName === 'OL')) {
          [...child.children].forEach((item) => {
            if (item.tagName === 'LI') blocks.push({ type: 'bullet', spans: inlineSpans(item) });
          });
        } else {
          const spans = inlineSpans(child);
          if (spans.length > 0) blocks.push({ type: 'paragraph', spans });
        }
      });
      return;
    }
    if (node.tagName === 'BR') {
      blocks.push({ type: 'paragraph', spans: [] });
      return;
    }
    blocks.push({ type: 'paragraph', spans: inlineSpans(node) });
  });
  return { blocks };
}

export function RichTextField({
  className,
  invalid,
  placeholder = 'Опишите подробнее…',
  resetKey,
  onChange,
  onKeyDown,
  ...props
}: RichTextFieldProps): ReactElement {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor === null) return;
    editor.replaceChildren();
    editor.dataset.empty = 'true';
  }, [resetKey]);

  const emit = (): void => {
    const editor = editorRef.current;
    if (editor === null) return;
    editor.dataset.empty = editor.textContent?.trim() ? 'false' : 'true';
    onChange(richTextDocumentFromElement(editor));
  };

  const format = (command: 'bold' | 'italic' | 'insertUnorderedList'): void => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    emit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'b' || event.key === 'i')) {
      event.preventDefault();
      format(event.key === 'b' ? 'bold' : 'italic');
    }
    onKeyDown?.(event);
  };

  return (
    <div
      className={['cards-kit-rich-text', className].filter(Boolean).join(' ')}
      data-invalid={invalid === true ? 'true' : undefined}
    >
      <div className="cards-kit-rich-text-toolbar" aria-label="Форматирование текста">
        <Button type="button" variant="ghost" size="compact" aria-label="Жирный" title="Жирный" onClick={() => format('bold')}>
          <PixelIcon name="bold" />
        </Button>
        <Button type="button" variant="ghost" size="compact" aria-label="Курсив" title="Курсив" onClick={() => format('italic')}>
          <PixelIcon name="italic" />
        </Button>
        <Button type="button" variant="ghost" size="compact" aria-label="Маркированный список" title="Маркированный список" onClick={() => format('insertUnorderedList')}>
          <PixelIcon name="bulletList" />
        </Button>
      </div>
      <div
        {...props}
        ref={editorRef}
        className="cards-kit-rich-text-editor"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-invalid={invalid || undefined}
        data-empty="true"
        data-placeholder={placeholder}
        onInput={emit}
        onKeyDown={handleKeyDown}
        onPaste={(event) => {
          event.preventDefault();
          document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
          emit();
        }}
      />
    </div>
  );
}
